import React from 'react'

import {
  BillingPlanInfoFragment,
  PaymentDetailsFragment,
} from '@sketch/gql-types'

import { formatPriceWithCurrency } from '@sketch/utils'

import { Spinner, AsyncButton, pluralize, Skeleton } from '@sketch/components'

import {
  SummaryHeaderWrapper,
  SummaryLineWrapper,
  SummaryTotalParagraph,
  BoldParagraph,
  Paragraph,
  PaymentLogo,
  PaymentInfoWrapper,
  Strong,
  PriceWrapper,
  AppliedDiscountRow,
  DiscountMessage,
  DiscountWrapper,
} from './Summary.styles'

import {
  getPlanDuration,
  isPaymentMethodCard,
  isPaymentMethodInvoice,
} from '../../utils'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { Currency } from '@sketch/gql-types/expansive'

type Price = number | 'Free'

interface LinePrice {
  currency: Currency
  amount: Price
}

const formatPrice = ({ amount, currency }: LinePrice) => {
  if (amount === 'Free') {
    return 'Free'
  }

  return formatPriceWithCurrency(amount, currency, { trimDecimalIfZero: false })
}

/**
 * Renders a small loading spinner for the summary prices
 */
const InlineLoading = () => (
  <>
    <Spinner size="1em" primary />
    &nbsp;&nbsp;
  </>
)

interface SummaryHeaderProps {
  plan?: BillingPlanInfoFragment
  customerCurrency?: Currency
  paymentMethod?: React.ReactNode
}

/**
 * SummaryHeader
 *
 * Renders the header for the Summary
 */
export const SummaryHeader: React.FCC<SummaryHeaderProps> = ({
  plan,
  customerCurrency,
  paymentMethod,
}) => {
  let content = null

  if (plan && plan.__typename === 'CloudBillingPlan') {
    const currentPlan = plan?.prices.find(
      price => price!.currency === customerCurrency
    )

    content = (
      <>
        <Strong>
          {currentPlan &&
            formatPriceWithCurrency(currentPlan.invoice, currentPlan.currency, {
              trimDecimalIfZero: false,
            })}
        </Strong>{' '}
        per Seat, per {getPlanDuration(plan.type)}
      </>
    )
  } else if (!plan) {
    content = <Skeleton width="100px" />
  }

  return (
    <SummaryHeaderWrapper>
      <div>
        <BoldParagraph>Summary</BoldParagraph>
        <Paragraph>{content}</Paragraph>
      </div>
      {paymentMethod}
    </SummaryHeaderWrapper>
  )
}

interface SummaryLineProps {
  description: React.ReactNode
  price?: LinePrice
  loading?: boolean
  'data-testid'?: string
  variant?: 'default' | 'partner'
}

/**
 * SummaryLine
 *
 * Renders a summary parcel
 */
export const SummaryLine: React.FCC<SummaryLineProps> = props => {
  const { description, price, loading, 'data-testid': dataTestId } = props

  return (
    <SummaryLineWrapper>
      <Paragraph>{description}</Paragraph>
      <Paragraph data-testid={dataTestId}>
        {loading && <InlineLoading />}
        {price !== undefined && <span>{formatPrice(price)}</span>}
      </Paragraph>
    </SummaryLineWrapper>
  )
}

export const SummaryTotalLine: React.FCC<SummaryLineProps> = props => {
  const {
    description,
    price,
    loading,
    'data-testid': dataTestId,
    variant,
  } = props

  return (
    <SummaryLineWrapper>
      <BoldParagraph>{description}</BoldParagraph>
      <PriceWrapper>
        <BoldParagraph data-testid={dataTestId}>
          {loading && <InlineLoading />}
          {price !== undefined && <span>{formatPrice(price)}</span>}
        </BoldParagraph>
        {!!price?.amount && (
          <>
            <Paragraph>Total exclusive of Tax</Paragraph>
            {variant === 'partner' && (
              <Paragraph>and Partner discounts</Paragraph>
            )}
          </>
        )}
      </PriceWrapper>
    </SummaryLineWrapper>
  )
}

interface SummaryErrorProps {
  refetch: () => Promise<any>
}

/**
 * SummaryError
 *
 * Renders the an error state with a retry button for the
 * summary
 */
export const SummaryError: React.FCC<SummaryErrorProps> = props => (
  <SummaryLineWrapper>
    <Paragraph>
      There was an error updating <br />
      the summary information.
    </Paragraph>
    <AsyncButton variant="primary" onClick={props.refetch}>
      <>Try Again</>
    </AsyncButton>
  </SummaryLineWrapper>
)

interface SummaryTotalDescribedProps {
  credit?: LinePrice
  charge?: LinePrice
}

/**
 * SummaryTotalDescribed
 *
 * Renders an text with the charged amount described
 */
export const SummaryTotalDescribed: React.FCC<
  SummaryTotalDescribedProps
> = props => {
  const { credit, charge } = props

  if (
    credit &&
    credit.amount !== 'Free' &&
    credit.amount > 0 &&
    charge &&
    charge.amount !== 'Free' &&
    charge.amount > 0
  ) {
    return (
      <SummaryTotalParagraph>
        {formatPrice(credit)} from Credit Balance, {formatPrice(charge)} from
        card.
      </SummaryTotalParagraph>
    )
  }

  return null
}

interface SummaryPaymentMethodProps {
  paymentType: 'CREDITS' | 'CREDITS-CHARGE_METHOD' | 'CHARGE_METHOD'
  chargeMethod: PaymentDetailsFragment
}

/**
 * SummaryPaymentMethod
 *
 * Renders the payments card logos according with the payment type supplied
 */
export const SummaryPaymentMethod: React.FCC<
  SummaryPaymentMethodProps
> = props => {
  const { paymentType, chargeMethod } = props
  let paymentContent = null

  if (paymentType === 'CREDITS') {
    paymentContent = (
      <>
        <PaymentLogo brand="credit" />
        &nbsp; Credits
      </>
    )
  } else if (
    paymentType === 'CREDITS-CHARGE_METHOD' &&
    isPaymentMethodCard(chargeMethod)
  ) {
    paymentContent = (
      <>
        <PaymentLogo brand="credit" />
        &nbsp;
        <PaymentLogo brand={chargeMethod.brand} />
      </>
    )
  } else if (
    paymentType === 'CREDITS-CHARGE_METHOD' &&
    isPaymentMethodInvoice(chargeMethod)
  ) {
    paymentContent = (
      <>
        <PaymentLogo brand="credit" />
        &nbsp;
        <PaymentLogo brand="invoice" />
      </>
    )
  } else if (
    paymentType === 'CHARGE_METHOD' &&
    isPaymentMethodCard(chargeMethod)
  ) {
    paymentContent = (
      <>
        <PaymentLogo brand={chargeMethod.brand} />
        &nbsp; ···{chargeMethod.last4Digits}
      </>
    )
  } else if (
    paymentType === 'CHARGE_METHOD' &&
    isPaymentMethodInvoice(chargeMethod)
  ) {
    paymentContent = (
      <>
        <PaymentLogo brand="invoice" />
        &nbsp; Invoice
      </>
    )
  }

  return (
    paymentContent && <PaymentInfoWrapper>{paymentContent}</PaymentInfoWrapper>
  )
}

interface SummaryDiscountProps {
  appliedDiscount: {
    code: string
    price: LinePrice
    percentOff: number | null
  }
  isAlreadyApplied?: boolean
  variant?: 'default' | 'partner'
}

type SummaryDiscountLineTextProps = Pick<
  SummaryDiscountProps,
  'appliedDiscount' | 'variant'
>

/**
 * SummaryDiscountLineText
 * Renders the discount line for regular or partner workspaces in the summary
 */
const SummaryDiscountLineText: React.FCC<SummaryDiscountLineTextProps> = ({
  variant,
  appliedDiscount,
}) => {
  if (variant === 'partner') {
    return (
      <SummaryLineWrapper>
        <Paragraph>
          Sketch Partner Discount: {appliedDiscount.percentOff}% Off
        </Paragraph>
        <Paragraph>
          {formatPrice({
            amount: -appliedDiscount.price.amount,
            currency: appliedDiscount.price.currency,
          })}
        </Paragraph>
      </SummaryLineWrapper>
    )
  }

  return (
    <AppliedDiscountRow>
      <Paragraph>{`${appliedDiscount.percentOff}% Off (${appliedDiscount.code})`}</Paragraph>
      <Paragraph>
        {formatPrice({
          amount: -appliedDiscount.price.amount,
          currency: appliedDiscount.price.currency,
        })}
      </Paragraph>
    </AppliedDiscountRow>
  )
}

/**
 * SummaryDiscountLine
 *
 * Renders the applied discounts in the summary
 */
export const SummaryDiscountLine: React.FCC<SummaryDiscountProps> = props => {
  const { appliedDiscount, isAlreadyApplied, variant = 'default' } = props

  return (
    <DiscountWrapper variant={variant}>
      {appliedDiscount && (
        <SummaryDiscountLineText
          appliedDiscount={appliedDiscount}
          variant={variant}
        />
      )}
      {isAlreadyApplied && variant !== 'partner' && (
        <DiscountMessage>
          We&apos;ve <strong>automatically</strong> applied this discount you
          previously used.
        </DiscountMessage>
      )}
    </DiscountWrapper>
  )
}

interface SummaryProratedProps {
  daysElapsedInCycle: number
  refundAmountForNewSeats: LinePrice
}

/**
 * SummaryProrated
 *
 * Renders Prorated info in the summary
 */
export const SummaryProrated: React.FCC<SummaryProratedProps> = ({
  daysElapsedInCycle,
  refundAmountForNewSeats,
}) => {
  return (
    <SummaryLineWrapper>
      <Paragraph>
        Unused period: {daysElapsedInCycle}{' '}
        {pluralize('day', 'days', daysElapsedInCycle)}
      </Paragraph>
      <Paragraph>
        {formatPrice({
          ...refundAmountForNewSeats,
          amount: -refundAmountForNewSeats.amount,
        })}
      </Paragraph>
    </SummaryLineWrapper>
  )
}
